import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@artosera/ui';
import { analyserCodeEtiquette } from '@artosera/shared';
import { reserveApi } from '../../api/reserve.js';
import { dansLaPorteeCourante } from '../../api/cles.js';
import { estApiError } from '../../api/errors.js';
import { useOeuvre } from '../../api/oeuvres.js';
import { useApercuMedia } from '../../api/medias.js';
import { VignetteOeuvre } from '../../composants/VignetteOeuvre.js';
import { useShellContext } from '../shell/context.js';
import { Scanner, type DecoderQrMultiple } from './Scanner.js';
import { signalerLecture } from './retourScan.js';
import styles from './InventairePage.module.css';

export type ArticlePanier = {
  oeuvreId: string;
  titre: string;
  artisteNom: string;
  numeroInventaire: string;
};

/** Le paramètre d'entrée de la liste des œuvres : les identifiants à présélectionner. */
export type SelectionScan = { selectionScan: string[] };

/**
 * « Sélection par scan » : on balaie plusieurs étiquettes, les œuvres s'ajoutent à un panier, puis
 * la liste des œuvres reçoit la sélection pour y appliquer les actions groupées.
 */
export function ScanSelection({ decoderQrMultiple }: { decoderQrMultiple?: DecoderQrMultiple }) {
  const { t } = useTranslation(['regie']);
  const { galerieSlug } = useShellContext();
  const navigate = useNavigate();
  const [panier, setPanier] = useState<ArticlePanier[]>([]);
  const [message, setMessage] = useState('');
  const [erreur, setErreur] = useState('');
  // Les jetons déjà traités : une étiquette gardée à l'écran ou relue ne repart pas au serveur.
  const traites = useRef(new Set<string>());
  const dansLePanier = useRef(new Set<string>());
  // Le jeton qui a fait entrer chaque œuvre : retirer l'une ne libère que celui-là.
  const jetonDe = useRef(new Map<string, string>());
  // Change à chaque « Vider » : une réponse partie avant ne doit pas remplir le panier d'après.
  const generation = useRef(0);

  async function lire(code: string): Promise<void> {
    const jeton = analyserCodeEtiquette(code);
    if (!jeton) {
      setErreur(t('regie:inventaire.selectionCodeInconnu'));
      return;
    }
    if (traites.current.has(jeton)) return;
    traites.current.add(jeton);
    const depart = generation.current;
    try {
      const { cible, resume } = await dansLaPorteeCourante((appel) =>
        reserveApi.resoudre(galerieSlug, jeton, appel),
      );
      if (depart !== generation.current) return;
      if (!('oeuvreId' in resume) || (cible.type !== 'oeuvre' && cible.type !== 'exemplaire')) {
        setErreur(t('regie:inventaire.selectionPasUneOeuvre'));
        return;
      }
      if (dansLePanier.current.has(resume.oeuvreId)) return;
      dansLePanier.current.add(resume.oeuvreId);
      jetonDe.current.set(resume.oeuvreId, jeton);
      setErreur('');
      setMessage(t('regie:inventaire.selectionAjoutee', { titre: resume.titre }));
      setPanier((avant) => [
        ...avant,
        {
          oeuvreId: resume.oeuvreId,
          titre: resume.titre,
          artisteNom: resume.artisteNom,
          numeroInventaire: resume.numeroInventaire,
        },
      ]);
      signalerLecture();
    } catch (e) {
      if (depart !== generation.current) return;
      const passager = !estApiError(e) || e.status >= 500 || e.status === 429;
      setErreur(estApiError(e) ? e.message : t('commun:erreurReseau'));
      // Un échec passager n'épuise pas l'étiquette : le lecteur réessaie. Un refus (étiquette
      // inconnue, droit) la garde traitée, sans quoi chaque image la redemanderait.
      if (passager) {
        traites.current.delete(jeton);
        throw e;
      }
    }
  }

  function retirer(oeuvreId: string): void {
    dansLePanier.current.delete(oeuvreId);
    const jeton = jetonDe.current.get(oeuvreId);
    if (jeton) traites.current.delete(jeton);
    jetonDe.current.delete(oeuvreId);
    setPanier((avant) => avant.filter((a) => a.oeuvreId !== oeuvreId));
  }

  function vider(): void {
    generation.current++;
    dansLePanier.current.clear();
    traites.current.clear();
    jetonDe.current.clear();
    setPanier([]);
    setMessage('');
  }

  function ouvrirDansLaListe(): void {
    const etat: SelectionScan = { selectionScan: panier.map((a) => a.oeuvreId) };
    navigate(`/${galerieSlug}/oeuvres`, { state: etat });
  }

  return (
    <>
      <p>{t('regie:inventaire.selectionInstructions')}</p>
      <Scanner multiple decoderQrMultiple={decoderQrMultiple} onScan={lire} />
      <section className={styles.panneau} aria-label={t('regie:inventaire.panier')}>
        <h2>{t('regie:inventaire.panierTitre', { count: panier.length })}</h2>
        {panier.length === 0 ? (
          <p>{t('regie:inventaire.panierVide')}</p>
        ) : (
          <ul className={styles.arbre}>
            {panier.map((article) => (
              <LignePanier key={article.oeuvreId} article={article} onRetirer={retirer} />
            ))}
          </ul>
        )}
        <div className={styles.actions}>
          <Button disabled={!panier.length} onClick={ouvrirDansLaListe}>
            {t('regie:inventaire.selectionOuvrir')}
          </Button>
          <Button variant="ghost" disabled={!panier.length} onClick={vider}>
            {t('regie:inventaire.selectionVider')}
          </Button>
        </div>
      </section>
      {erreur && (
        <p role="alert" className={styles.erreur}>
          {erreur}
        </p>
      )}
      <p role="status">{message}</p>
    </>
  );
}

function LignePanier({
  article,
  onRetirer,
}: {
  article: ArticlePanier;
  onRetirer: (oeuvreId: string) => void;
}) {
  const { t } = useTranslation(['regie']);
  const { galerieSlug } = useShellContext();
  const fiche = useOeuvre(galerieSlug, article.oeuvreId);
  const mediaId = fiche.data?.oeuvre.mediaPrincipalId ?? null;
  const apercu = useApercuMedia(galerieSlug, mediaId);
  return (
    <li className={styles.ligne}>
      <VignetteOeuvre url={apercu.data ?? null} taille="md" />
      <div className={styles.articlePanier}>
        <strong>{article.titre}</strong>
        <p>
          {article.artisteNom} · {article.numeroInventaire}
        </p>
      </div>
      <Button
        variant="ghost"
        aria-label={t('regie:inventaire.retirerOeuvre', { titre: article.titre })}
        onClick={() => onRetirer(article.oeuvreId)}
      >
        {t('regie:inventaire.retirer')}
      </Button>
    </li>
  );
}
